import { Button, toast } from "@gotts.ai/ui";
import { useState } from "react";

import { TokenIcon } from "../../components/TokenIcon";
import { useDeployment, useWallet } from "../../context";
import { useTransactionFlow } from "../../hooks/useTransactionFlow";
import { useTxFeedback } from "../../hooks/useTxFeedback";
import {
  MOCK_ERC20_ABI,
  POOL_MODIFY_LIQUIDITY_TEST_ABI,
  V2_ROUTER_ABI,
  V3_NFT_POSITION_MANAGER_ABI,
} from "../../lib/abis";
import {
  deadline,
  DEVENV_TOKEN_PRICES,
  MAX_UINT256,
} from "../../lib/constants";
import { TOKEN_DECIMALS } from "../../lib/constants";
import { formatTokenAmount, formatUsd } from "../../lib/format";

import { TransactionStepper } from "./TransactionStepper";
import {
  computePositionAmounts,
  MAX_UINT128,
  resolveSymbol,
  tickToPrice,
} from "./types";

import type { StepDefinition } from "../../hooks/useTransactionFlow";
import type { V2Position, V3Position, V4Position } from "./types";
import type { Address } from "viem";
import type { Hex } from "viem";

interface RemoveLiquidityModalProps {
  type: "V2" | "V3" | "V4";
  v2Position?: V2Position;
  v3Position?: V3Position;
  v4Position?: V4Position;
  currentTick?: number;
  onClose: () => void;
  onSuccess: () => void;
}

const PERCENTAGE_OPTIONS = [25, 50, 75, 100] as const;

/** Get tick bounds for V3 or V4 position */
function getTickBounds(
  type: "V2" | "V3" | "V4",
  v3Position?: V3Position,
  v4Position?: V4Position,
): { tickLower: number; tickUpper: number } | null {
  if (type === "V3" && v3Position)
    return { tickLower: v3Position.tickLower, tickUpper: v3Position.tickUpper };
  if (type === "V4" && v4Position)
    return { tickLower: v4Position.tickLower, tickUpper: v4Position.tickUpper };
  return null;
}

export function RemoveLiquidityModal({
  type,
  v2Position,
  v3Position,
  v4Position,
  currentTick,
  onClose,
  onSuccess,
}: RemoveLiquidityModalProps) {
  const { deployment } = useDeployment();
  const { walletClient, address, chain } = useWallet();
  const flow = useTransactionFlow();
  const { feedbackClass, onTxSuccess, onTxError } = useTxFeedback();
  const [percentage, setPercentage] = useState(100);
  const [completed, setCompleted] = useState(false);

  const tokens = (deployment?.tokens ?? {}) as unknown as Record<
    string,
    Address
  >;

  const tickBounds = getTickBounds(type, v3Position, v4Position);
  const isInRange =
    tickBounds !== null &&
    currentTick !== undefined &&
    currentTick >= tickBounds.tickLower &&
    currentTick < tickBounds.tickUpper;

  function getEstimatedAmounts(): {
    amount0: string;
    amount1: string;
    num0: number;
    num1: number;
    sym0: string;
    sym1: string;
    dec0: number;
    dec1: number;
  } {
    if (type === "V2" && v2Position) {
      const sym0 = resolveSymbol(v2Position.token0, tokens);
      const sym1 = resolveSymbol(v2Position.token1, tokens);
      const dec0 = TOKEN_DECIMALS[sym0] ?? 18;
      const dec1 = TOKEN_DECIMALS[sym1] ?? 18;
      const share0 = (v2Position.userShare0 * BigInt(percentage)) / 100n;
      const share1 = (v2Position.userShare1 * BigInt(percentage)) / 100n;
      const n0 = Number(share0) / 10 ** dec0;
      const n1 = Number(share1) / 10 ** dec1;
      return {
        amount0: formatTokenAmount(share0, dec0).display,
        amount1: formatTokenAmount(share1, dec1).display,
        num0: n0,
        num1: n1,
        sym0,
        sym1,
        dec0,
        dec1,
      };
    }
    if (type === "V3" && v3Position) {
      const sym0 = resolveSymbol(v3Position.token0, tokens);
      const sym1 = resolveSymbol(v3Position.token1, tokens);
      const dec0 = TOKEN_DECIMALS[sym0] ?? 18;
      const dec1 = TOKEN_DECIMALS[sym1] ?? 18;
      if (currentTick !== undefined) {
        const liqForPct = (v3Position.liquidity * BigInt(percentage)) / 100n;
        const { amount0, amount1 } = computePositionAmounts(
          liqForPct,
          v3Position.tickLower,
          v3Position.tickUpper,
          currentTick,
          dec0,
          dec1,
        );
        return {
          amount0: formatTokenAmount(
            BigInt(Math.round(amount0 * 10 ** dec0)),
            dec0,
          ).display,
          amount1: formatTokenAmount(
            BigInt(Math.round(amount1 * 10 ** dec1)),
            dec1,
          ).display,
          num0: amount0,
          num1: amount1,
          sym0,
          sym1,
          dec0,
          dec1,
        };
      }
      return {
        amount0: "Varies by price",
        amount1: "Varies by price",
        num0: 0,
        num1: 0,
        sym0,
        sym1,
        dec0,
        dec1,
      };
    }
    if (type === "V4" && v4Position) {
      const sym0 = resolveSymbol(v4Position.currency0, tokens);
      const sym1 = resolveSymbol(v4Position.currency1, tokens);
      const dec0 = TOKEN_DECIMALS[sym0] ?? 18;
      const dec1 = TOKEN_DECIMALS[sym1] ?? 18;
      if (currentTick !== undefined) {
        const liqForPct = (v4Position.liquidity * BigInt(percentage)) / 100n;
        const { amount0, amount1 } = computePositionAmounts(
          liqForPct,
          v4Position.tickLower,
          v4Position.tickUpper,
          currentTick,
          dec0,
          dec1,
        );
        return {
          amount0: formatTokenAmount(
            BigInt(Math.round(amount0 * 10 ** dec0)),
            dec0,
          ).display,
          amount1: formatTokenAmount(
            BigInt(Math.round(amount1 * 10 ** dec1)),
            dec1,
          ).display,
          num0: amount0,
          num1: amount1,
          sym0,
          sym1,
          dec0,
          dec1,
        };
      }
      return {
        amount0: "Varies by price",
        amount1: "Varies by price",
        num0: 0,
        num1: 0,
        sym0,
        sym1,
        dec0,
        dec1,
      };
    }
    return {
      amount0: "0",
      amount1: "0",
      num0: 0,
      num1: 0,
      sym0: "?",
      sym1: "?",
      dec0: 18,
      dec1: 18,
    };
  }

  async function handleRemove() {
    if (!walletClient || !address || !deployment) return;

    const steps: StepDefinition[] = [];

    if (type === "V2" && v2Position) {
      const router = deployment.v2.router02;
      const lpAmount = (v2Position.lpBalance * BigInt(percentage)) / 100n;
      const wethAddr = deployment.tokens?.weth;
      const isWethPairA =
        v2Position.token0.toLowerCase() === wethAddr?.toLowerCase();
      const isWethPairB =
        v2Position.token1.toLowerCase() === wethAddr?.toLowerCase();
      const isWethPair = isWethPairA || isWethPairB;

      steps.push({
        label: "Approve LP Token",
        execute: async () =>
          walletClient.writeContract({
            chain,
            account: address,
            address: v2Position.pairAddress,
            abi: MOCK_ERC20_ABI,
            functionName: "approve",
            args: [router, MAX_UINT256],
          }),
      });

      if (isWethPair) {
        const otherToken = isWethPairA ? v2Position.token1 : v2Position.token0;
        steps.push({
          label: "Remove Liquidity",
          execute: async () =>
            walletClient.writeContract({
              chain,
              account: address,
              address: router,
              abi: V2_ROUTER_ABI,
              functionName: "removeLiquidityETH",
              args: [otherToken, lpAmount, 0n, 0n, address, deadline()],
            }),
        });
      } else {
        steps.push({
          label: "Remove Liquidity",
          execute: async () =>
            walletClient.writeContract({
              chain,
              account: address,
              address: router,
              abi: V2_ROUTER_ABI,
              functionName: "removeLiquidity",
              args: [
                v2Position.token0,
                v2Position.token1,
                lpAmount,
                0n,
                0n,
                address,
                deadline(),
              ],
            }),
        });
      }
    } else if (type === "V3" && v3Position) {
      const npm = deployment.v3.nonfungiblePositionManager;
      const liquidityToRemove =
        (v3Position.liquidity * BigInt(percentage)) / 100n;

      steps.push({
        label: "Decrease Liquidity",
        execute: async () =>
          walletClient.writeContract({
            chain,
            account: address,
            address: npm,
            abi: V3_NFT_POSITION_MANAGER_ABI,
            functionName: "decreaseLiquidity",
            args: [
              {
                tokenId: v3Position.tokenId,
                liquidity: liquidityToRemove,
                amount0Min: 0n,
                amount1Min: 0n,
                deadline: deadline(),
              },
            ],
          }),
      });

      steps.push({
        label: "Collect Tokens",
        execute: async () =>
          walletClient.writeContract({
            chain,
            account: address,
            address: npm,
            abi: V3_NFT_POSITION_MANAGER_ABI,
            functionName: "collect",
            args: [
              {
                tokenId: v3Position.tokenId,
                recipient: address,
                amount0Max: MAX_UINT128,
                amount1Max: MAX_UINT128,
              },
            ],
          }),
      });
    } else if (type === "V4" && v4Position) {
      const modLiq = deployment.v4.poolModifyLiquidityTest;
      const liquidityToRemove =
        (v4Position.liquidity * BigInt(percentage)) / 100n;

      steps.push({
        label: "Remove V4 Liquidity",
        execute: async () =>
          walletClient.writeContract({
            chain,
            account: address,
            address: modLiq,
            abi: POOL_MODIFY_LIQUIDITY_TEST_ABI,
            functionName: "modifyLiquidity",
            args: [
              {
                currency0: v4Position.currency0,
                currency1: v4Position.currency1,
                fee: v4Position.fee,
                tickSpacing: v4Position.tickSpacing,
                hooks: v4Position.hooks,
              },
              {
                tickLower: v4Position.tickLower,
                tickUpper: v4Position.tickUpper,
                liquidityDelta: -liquidityToRemove,
                salt: v4Position.salt,
              },
              "0x" as Hex,
            ],
          }),
      });
    }

    const toastId = toast.loading(
      `Removing ${String(percentage)}% liquidity...`,
    );
    await flow.execute(
      steps,
      () => {
        toast.dismiss(toastId);
        onTxSuccess();
        onSuccess();
        setCompleted(true);
      },
      (_label, _err) => {
        onTxError();
      },
    );
    toast.dismiss(toastId);
  }

  const est = getEstimatedAmounts();

  // USD estimates
  const usd0 = est.num0 * (DEVENV_TOKEN_PRICES[est.sym0] ?? 0);
  const usd1 = est.num1 * (DEVENV_TOKEN_PRICES[est.sym1] ?? 0);
  const totalUsd = usd0 + usd1;
  const hasUsd = totalUsd > 0;

  // Token allocation percentage (for stacked bar)
  const alloc0Pct = hasUsd ? Math.round((usd0 / totalUsd) * 100) : 50;

  // Range bar position (where currentTick falls relative to tickLower..tickUpper)
  const rangeBarPct =
    tickBounds !== null && currentTick !== undefined
      ? Math.max(
          0,
          Math.min(
            100,
            ((currentTick - tickBounds.tickLower) /
              (tickBounds.tickUpper - tickBounds.tickLower)) *
              100,
          ),
        )
      : null;

  // V3 uncollected fees
  const hasUncollectedFees =
    type === "V3" &&
    v3Position !== undefined &&
    (v3Position.tokensOwed0 > 0n || v3Position.tokensOwed1 > 0n);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      data-testid="remove-liquidity-modal"
    >
      <div
        className="animate-backdrop-in absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`animate-modal-in glass-card relative z-10 w-full max-w-sm p-6 shadow-2xl ${feedbackClass}`}
      >
        <div className="section-tag">Remove {type} Liquidity</div>

        {/* Percentage selector */}
        <div className="mb-4">
          <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-muted)]">
            Amount to remove
          </label>
          <div className="flex gap-2">
            {PERCENTAGE_OPTIONS.map((pct) => (
              <button
                key={pct}
                type="button"
                onClick={() => {
                  setPercentage(pct);
                }}
                className={`btn-percentage-pill flex-1 px-3 py-2 text-sm ${
                  percentage === pct ? "btn-percentage-pill-selected" : ""
                }`}
                data-testid={`remove-pct-${String(pct)}`}
              >
                {pct}%
              </button>
            ))}
          </div>
        </div>

        {/* Estimated return */}
        <div className="mb-4 rounded border border-[var(--glass-border)] bg-[rgba(0,0,0,0.15)] p-3">
          <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-muted)]">
            Estimated return
          </span>

          {/* In-range badge for V3/V4 */}
          {(type === "V3" || type === "V4") &&
            tickBounds !== null &&
            currentTick !== undefined && (
              <div className="mb-2" data-testid="range-badge">
                <span
                  className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    isInRange
                      ? "bg-[rgba(52,209,106,0.15)] text-[var(--color-success)]"
                      : "bg-[rgba(239,68,68,0.15)] text-[#ef4444]"
                  }`}
                >
                  {isInRange ? "\u25CF In Range" : "\u25CF Out of Range"}
                </span>
              </div>
            )}

          {/* Range bar for V3/V4 */}
          {(type === "V3" || type === "V4") &&
            tickBounds !== null &&
            currentTick !== undefined && (
              <div className="mb-3" data-testid="range-bar">
                <div className="relative h-1.5 w-full rounded-full bg-[rgba(255,255,255,0.08)]">
                  {/* Active range fill */}
                  <div
                    className={`absolute inset-0 rounded-full ${isInRange ? "bg-[rgba(52,209,106,0.3)]" : "bg-[rgba(239,68,68,0.2)]"}`}
                  />
                  {/* Current tick marker */}
                  <div
                    className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-[var(--color-gold)]"
                    style={{ left: `${String(rangeBarPct)}%` }}
                    data-testid="tick-marker"
                  />
                </div>
                {/* Price range labels */}
                <div className="mt-1 flex justify-between text-[10px] text-[var(--color-text-muted)]">
                  <span>
                    Min: {tickToPrice(tickBounds.tickLower, est.dec0, est.dec1)}{" "}
                    {est.sym1}/{est.sym0}
                  </span>
                  <span>
                    Max: {tickToPrice(tickBounds.tickUpper, est.dec0, est.dec1)}{" "}
                    {est.sym1}/{est.sym0}
                  </span>
                </div>
              </div>
            )}

          {/* Token amounts */}
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5">
              <TokenIcon symbol={est.sym0} size={16} />
              <span className="text-text-muted">{est.sym0}</span>
            </span>
            <span className="text-text font-mono">{est.amount0}</span>
          </div>
          <div className="mt-1.5 flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5">
              <TokenIcon symbol={est.sym1} size={16} />
              <span className="text-text-muted">{est.sym1}</span>
            </span>
            <span className="text-text font-mono">{est.amount1}</span>
          </div>

          {/* Token allocation bar */}
          {hasUsd && (
            <div className="mt-2.5" data-testid="allocation-bar">
              <div className="flex h-1 w-full overflow-hidden rounded-full">
                <div
                  className="bg-[var(--color-gold)] transition-all"
                  style={{ width: `${String(alloc0Pct)}%` }}
                />
                <div
                  className="bg-[rgba(99,179,237,0.6)] transition-all"
                  style={{ width: `${String(100 - alloc0Pct)}%` }}
                />
              </div>
              <div className="mt-0.5 flex justify-between text-[10px] text-[var(--color-text-muted)]">
                <span>
                  {est.sym0} {String(alloc0Pct)}%
                </span>
                <span>
                  {est.sym1} {String(100 - alloc0Pct)}%
                </span>
              </div>
            </div>
          )}

          {/* V3 uncollected fees */}
          {hasUncollectedFees && (
            <div
              className="mt-2 border-t border-[var(--glass-border)] pt-2 text-xs"
              data-testid="uncollected-fees"
            >
              <span className="text-text-muted">Uncollected Fees</span>
              <div className="mt-0.5 flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <TokenIcon symbol={est.sym0} size={14} />
                  <span className="text-text-muted">{est.sym0}</span>
                </span>
                <span className="font-mono">
                  {formatTokenAmount(v3Position.tokensOwed0, est.dec0).display}
                </span>
              </div>
              <div className="mt-0.5 flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <TokenIcon symbol={est.sym1} size={14} />
                  <span className="text-text-muted">{est.sym1}</span>
                </span>
                <span className="font-mono">
                  {formatTokenAmount(v3Position.tokensOwed1, est.dec1).display}
                </span>
              </div>
            </div>
          )}

          {/* USD estimate */}
          {hasUsd && (
            <div
              className="mt-2 flex items-center justify-between border-t border-[var(--glass-border)] pt-2 text-xs"
              data-testid="usd-estimate"
            >
              <span className="text-text-muted">Est. Value</span>
              <span className="font-mono font-medium text-[var(--color-gold)]">
                {formatUsd(totalUsd)}
              </span>
            </div>
          )}
        </div>

        {/* V3/V4 slippage note (only when we couldn't compute amounts) */}
        {(type === "V3" || type === "V4") && currentTick === undefined && (
          <p className="type-supporting mb-2">
            {type} amounts depend on the current pool price. Dollar value will
            be determined at time of withdrawal. Slippage may apply.
          </p>
        )}

        {/* Transaction stepper */}
        <TransactionStepper steps={flow.steps} />

        {completed && (
          <div className="animate-success-in my-4 rounded border border-[rgba(52,209,106,0.3)] bg-[rgba(52,209,106,0.05)] p-4 text-center">
            <span className="mb-2 block text-2xl">{"\u2713"}</span>
            <p className="text-sm font-medium text-[var(--color-success)]">
              Liquidity removed successfully
            </p>
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">
              Received {est.amount0} {est.sym0} + {est.amount1} {est.sym1}
            </p>
          </div>
        )}

        {/* Buttons */}
        <div className="mt-4 flex gap-3">
          {completed ? (
            <Button
              variant="gold"
              size="sm"
              className="flex-1"
              onClick={onClose}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={onClose}
                disabled={flow.isExecuting}
              >
                Cancel
              </Button>
              <Button
                variant="gold"
                size="sm"
                className="flex-1"
                onClick={handleRemove}
                disabled={flow.isExecuting}
                loading={flow.isExecuting}
              >
                {flow.isExecuting
                  ? "Removing..."
                  : `Remove ${String(percentage)}%`}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
